import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, StickyNote } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Checkbox, Field, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { StoreSelect, invalidateStockControl, useStoreOptions } from './stock-control-common';

const STOCK_TAKE_TAB = '/stock-control?tab=stock-take';

interface CreatedSheet {
  id: string;
  number: string;
  lineCount: number;
  blind: boolean;
}

export function NewCountSheetPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), STOCK_TAKE_TAB);
  const stores = useStoreOptions();
  const initialStoreId = params.get('storeId') ?? '';
  const [storeId, setStoreId] = useState(initialStoreId);
  const [blind, setBlind] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => post<CreatedSheet>('/pharmacy/stock-takes', { storeId, blind, notes: notes.trim() || undefined }),
    onSuccess: (sheet) => {
      toast.success(
        tr('pharmacy', 'count_sheet_x_started', { number: sheet.number }),
        `${sheet.lineCount} batch${sheet.lineCount === 1 ? '' : 'es'} to count${sheet.blind ? ' · blind count' : ''}`,
      );
      void invalidateStockControl(queryClient);
      navigate(`${STOCK_TAKE_TAB}&sheet=${sheet.id}`, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const dirty = !create.isSuccess && (storeId !== initialStoreId || blind || notes !== '');

  return (
    <FormScreen
      title={tr('pharmacy', 'new_count_sheet')}
      description={tr('pharmacy', 'takes_a_snapshot_of_every_batch_on_the_shelf_in')}
      eyebrow={tr('pharmacy', 'stock_control')}
      icon={ClipboardCheck}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!storeId) return;
        setError(null);
        create.mutate();
      }}
    >
      <FormSection title={tr('opd', 'details')} icon={ClipboardCheck} columns={1}>
        <Field label={tr('pharmacy', 'store')} required>
          <StoreSelect value={storeId} onChange={setStoreId} stores={stores.data} emptyLabel={tr('pharmacy', 'select')} />
        </Field>
        <Checkbox
          checked={blind}
          onCheckedChange={setBlind}
          label={tr('pharmacy', 'blind_count')}
          description={tr('pharmacy', 'counters_do_not_see_the_system_quantity_so_they')}
        />
      </FormSection>

      <FormSection title={tr('pharmacy', 'notes')} icon={StickyNote} columns={1}>
        <Field label={tr('pharmacy', 'notes')} hint={tr('pharmacy', 'optional')}>
          <Textarea rows={2} maxLength={600} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('pharmacy', 'start_counting')} loading={create.isPending} incomplete={!storeId} cancelTo={returnTo} />
    </FormScreen>
  );
}
